import type {
  CellViolationInfo,
  ViolationGroupInfo,
  DetailedViolation,
  BuildCellMapOptions,
  NurseForCellMap,
} from "./types";
import {
  buildShiftIdToGroupMainLetterMap,
  buildShiftLookupMaps,
  getManpowerBucketForResolvedShift,
  resolveShiftFromScheduleCellFromMaps,
  type ShiftLookupMaps,
} from "@/utils/shift-util";

type AvailableShiftItem = BuildCellMapOptions["availableShifts"][number];

/**
 * 일별 그룹 합계 계산 (manpower_shortage 셀 매핑에 필요)
 * validateManpower와 동일한 id 기반 해석으로 통계와 일치시킨다.
 * Map<"dayIndex-defaultShift", count>
 */
function buildGroupTotalsByDay(
  nurses: NurseForCellMap[],
  lookupMaps: ShiftLookupMaps<AvailableShiftItem>,
  daysInMonth: number,
  precepteePolicy: BuildCellMapOptions["precepteePolicy"],
  manpowerAgg?: {
    validateKeys: Set<string>;
    groupLetterMap: Map<string, string>;
  },
  nurseLockedDaysMap?: Map<string, Set<number>>
): Map<string, number> {
  const totals = new Map<string, number>();

  const nursesToUse = precepteePolicy.excludePrecepteeFromCount
    ? nurses.filter((n) => n.preceptor?.role !== "preceptee")
    : nurses;

  for (let dayIndex = 0; dayIndex < daysInMonth; dayIndex++) {
    const groupCounts = new Map<string, number>();

    nursesToUse.forEach((n) => {
      const nurseId = n.nurse_id || n.id;
      // 잠금 day는 manpower 통계에서 제외
      if (nurseId && nurseLockedDaysMap) {
        const locked = nurseLockedDaysMap.get(nurseId);
        if (locked?.has(dayIndex + 1)) return; // 1-based
      }

      const schedule = n.schedule;
      if (schedule && schedule[dayIndex]) {
        const code = schedule[dayIndex]!;
        const scheduleId = n.schedule_ids?.[dayIndex] ?? null;
        const resolved = resolveShiftFromScheduleCellFromMaps(
          code,
          scheduleId,
          lookupMaps
        );
        const bucket = manpowerAgg
          ? getManpowerBucketForResolvedShift(
              resolved,
              manpowerAgg.groupLetterMap,
              manpowerAgg.validateKeys
            )
          : resolved?.default_shift ?? null;
        if (bucket) {
          groupCounts.set(bucket, (groupCounts.get(bucket) ?? 0) + 1);
        }
      }
    });

    groupCounts.forEach((count, shiftKey) => {
      totals.set(`${dayIndex}-${shiftKey}`, count);
    });
  }

  return totals;
}

/**
 * 위반 타입별 패턴명 반환
 */
function getPatternName(pattern?: string): string {
  switch (pattern) {
    case "ED":
      return "E 다음 날 D";
    case "ND":
      return "N 다음 날 D";
    case "NE":
      return "N 다음 날 E";
    default:
      return "E 다음 날 D";
  }
}

/**
 * 위반의 영향 범위 (시작일, 종료일)를 반환
 */
function getViolationRange(v: DetailedViolation): {
  startDay: number;
  endDay: number;
} | null {
  switch (v.type) {
    case "max_conseq_work":
    case "three_seq_nig":
      if (v.start_day !== undefined && v.end_day !== undefined) {
        return { startDay: v.start_day, endDay: v.end_day };
      }
      return null;

    case "banned_day_after_eve":
      if (v.day !== undefined && v.next_day !== undefined) {
        return { startDay: v.day, endDay: v.next_day };
      }
      return null;

    case "two_offs_after_two_nig":
      if (v.start_day !== undefined) {
        return {
          startDay: v.start_day,
          endDay: v.off_end_day ?? v.end_day ?? v.start_day + 3,
        };
      }
      return null;

    case "two_offs_after_three_nig":
      if (v.start_day !== undefined) {
        return {
          startDay: v.start_day,
          endDay: v.off_end_day ?? v.end_day ?? v.start_day + 4,
        };
      }
      return null;

    default:
      return null;
  }
}

/**
 * 위반에 대한 툴팁 메시지를 생성
 */
function buildViolationMessage(
  v: DetailedViolation,
  nurseName: string,
  dayIndex: number,
  // manpower용
  groupTotal?: number,
  formatDayLabel?: (idx?: number) => string
): string | null {
  const getDay = (idx?: number) => {
    if (formatDayLabel) return formatDayLabel(idx);
    return typeof idx === "number" ? `${idx + 1}일` : "-";
  };

  switch (v.type) {
    case "manpower_shortage":
      if (v.day === dayIndex && v.shift !== undefined) {
        return `${getDay(v.day)} ${v.shift} 근무 인원 미달 (필요: ${v.required}, 배정: ${groupTotal ?? v.actual})`;
      }
      return null;
    case "manpower_excess":
      if (v.day === dayIndex && v.shift !== undefined) {
        return `${getDay(v.day)} ${v.shift} 근무 인원 초과 (최대: ${v.required_max}, 배정: ${groupTotal ?? v.actual})`;
      }
      return null;

    case "max_conseq_work":
      if (
        v.start_day !== undefined &&
        v.end_day !== undefined &&
        dayIndex >= v.start_day &&
        dayIndex <= v.end_day
      ) {
        return `${nurseName} 연속 근무일 초과 (최대: ${v.max}일, 현재: ${v.actual}일)`;
      }
      return null;

    case "three_seq_nig":
      if (
        v.start_day !== undefined &&
        v.end_day !== undefined &&
        dayIndex >= v.start_day &&
        dayIndex <= v.end_day
      ) {
        return `${nurseName} N 3연속 근무 위반 (${getDay(v.start_day)}~${getDay(v.end_day)})`;
      }
      return null;

    case "banned_day_after_eve":
      if (v.day === dayIndex || v.next_day === dayIndex) {
        const patternName = getPatternName(v.pattern);
        return `${nurseName} ${patternName} 근무 위반 (${getDay(v.day)} → ${getDay(v.next_day)})`;
      }
      return null;

    case "max_nig_per_month":
      return `${nurseName} 월 나이트 근무 초과 (최대: ${v.max}회, 현재: ${v.actual}회)`;

    case "two_offs_after_two_nig": {
      const range = getViolationRange(v);
      if (range && dayIndex >= range.startDay && dayIndex <= range.endDay) {
        return `${nurseName} N 2회 후 OFF 2일 미준수 (${getDay(v.start_day)}~${getDay(typeof v.start_day === "number" ? v.start_day + 1 : undefined)} N 후 ${getDay(typeof v.start_day === "number" ? v.start_day + 2 : undefined)}~${getDay(typeof v.start_day === "number" ? v.start_day + 3 : undefined)} OFF 필요)`;
      }
      return null;
    }

    case "two_offs_after_three_nig": {
      const range = getViolationRange(v);
      if (range && dayIndex >= range.startDay && dayIndex <= range.endDay) {
        return `${nurseName} N 3회 후 OFF 2일 미준수 (${getDay(v.start_day)}~${getDay(typeof v.start_day === "number" ? v.start_day + 2 : undefined)} N 후 ${getDay(typeof v.start_day === "number" ? v.start_day + 3 : undefined)} OFF 필요)`;
      }
      return null;
    }

    default:
      return null;
  }
}

/**
 * 교차월 위반의 바를 현재월/전월 경계(0)에서 분할하는 헬퍼.
 * - 전월 셀(dayIndex < 0): 전월 영역만 바로 표시 (startDay ~ min(endDay, -1))
 * - 현재월 셀(dayIndex >= 0): 현재월 영역에서 새 바 시작 (max(startDay, 0) ~ endDay)
 */
function clampGroupToZone(
  type: string,
  startDay: number,
  endDay: number,
  dayIndex: number,
  pattern?: string,
  tailStart = 0,
  daysInMonth = 0
): ViolationGroupInfo | null {
  // 화면에 존재하는 인덱스 범위 내로 강제 클램핑
  // - 음수(dayIndex)는 전월 tail 영역: [-tailStart ~ -1]
  // - 0 이상(dayIndex)은 현재월 영역: [0 ~ daysInMonth-1]
  const clampedGlobalStart = Math.max(startDay, -tailStart);
  const clampedGlobalEnd = Math.min(endDay, daysInMonth - 1);

  // 범위가 화면 밖이면 표시할 그룹 없음
  if (clampedGlobalEnd < clampedGlobalStart) return null;

  startDay = clampedGlobalStart;
  endDay = clampedGlobalEnd;

  // 교차월이 아닌 경우 (같은 영역 내): 클램핑 불필요
  if ((startDay >= 0 && dayIndex >= 0) || (endDay < 0 && dayIndex < 0)) {
    return {
      type,
      pattern,
      startDay,
      endDay,
      rangeLength: endDay - startDay + 1,
      isStart: dayIndex === startDay,
      isEnd: dayIndex === endDay,
    };
  }

  // 교차월: 현재월 셀에서 바를 0부터 시작
  if (dayIndex >= 0) {
    const clampedStart = Math.max(startDay, 0);
    const clampedEnd = endDay; // 이미 global clamp로 daysInMonth-1 이하임
    // 위반 시작점이 전월(tail)에서 왔다면,
    // 현재월에서 또 "시작 바"를 찍지 말고 전월 시작 셀의 바가 이어지도록 합니다.
    const startedInTail = startDay < 0;
    return {
      type,
      pattern,
      startDay: clampedStart,
      endDay: clampedEnd,
      rangeLength: clampedEnd - clampedStart + 1,
      isStart: !startedInTail && dayIndex === clampedStart,
      isEnd: dayIndex === clampedEnd,
    };
  }

  // 교차월: 전월 셀에서 바를 -1까지만
  // started in tail인 바가 현재월까지 이어져야 하므로 endDay를 잘라내지 않습니다.
  const clampedEnd = endDay;
  return {
    type,
    pattern,
    startDay,
    endDay: clampedEnd,
    rangeLength: clampedEnd - startDay + 1,
    isStart: dayIndex === startDay,
    isEnd: dayIndex === clampedEnd,
  };
}

/**
 * 위반에 대한 시각적 그룹 정보를 생성 (특정 셀 기준)
 */
function buildViolationGroup(
  v: DetailedViolation,
  dayIndex: number,
  // manpower용
  groupTotal?: number,
  required?: number,
  tailStart = 0,
  daysInMonth = 0
): ViolationGroupInfo | null {
  switch (v.type) {
    case "manpower_shortage":
      // groupTotal < required인 경우에만 표시
      if (
        v.day === dayIndex &&
        groupTotal !== undefined &&
        required !== undefined &&
        groupTotal < required
      ) {
        return {
          type: v.type,
          startDay: dayIndex,
          endDay: dayIndex,
          rangeLength: 1,
          isStart: true,
          isEnd: true,
        };
      }
      return null;
    case "manpower_excess":
      if (
        v.day === dayIndex &&
        groupTotal !== undefined &&
        v.required_max !== undefined &&
        groupTotal > v.required_max
      ) {
        return {
          type: v.type,
          startDay: dayIndex,
          endDay: dayIndex,
          rangeLength: 1,
          isStart: true,
          isEnd: true,
        };
      }
      return null;

    case "banned_day_after_eve": {
      if (v.day === dayIndex || v.next_day === dayIndex) {
        const sd = v.day ?? dayIndex;
        const ed = v.next_day ?? dayIndex;
        return clampGroupToZone(
          v.type,
          sd,
          ed,
          dayIndex,
          v.pattern,
          tailStart,
          daysInMonth
        );
      }
      return null;
    }

    case "max_conseq_work":
    case "three_seq_nig": {
      const range = getViolationRange(v);
      if (range && dayIndex >= range.startDay && dayIndex <= range.endDay) {
        return clampGroupToZone(
          v.type,
          range.startDay,
          range.endDay,
          dayIndex,
          undefined,
          tailStart,
          daysInMonth
        );
      }
      return null;
    }

    case "two_offs_after_two_nig":
    case "two_offs_after_three_nig": {
      const range = getViolationRange(v);
      if (range && dayIndex >= range.startDay && dayIndex <= range.endDay) {
        return clampGroupToZone(
          v.type,
          range.startDay,
          range.endDay,
          dayIndex,
          undefined,
          tailStart,
          daysInMonth
        );
      }
      return null;
    }

    // max_nig_per_month는 시각적 그룹 바를 생성하지 않음
    default:
      return null;
  }
}

/**
 * Map 키에서 셀 위반 정보를 가져오거나 초기화
 */
function getOrCreateCell(
  map: Map<string, CellViolationInfo>,
  key: string
): CellViolationInfo {
  let cell = map.get(key);
  if (!cell) {
    cell = { isError: false, messages: [], groups: [] };
    map.set(key, cell);
  }
  return cell;
}

/**
 * groups 배열에 중복 그룹이 있는지 확인 (같은 type + startDay)
 */
function hasGroupDuplicate(
  groups: ViolationGroupInfo[],
  group: ViolationGroupInfo
): boolean {
  return groups.some(
    (g) => g.type === group.type && g.startDay === group.startDay
  );
}

// ─── 메인 함수 ───────────────────────────────────────────────────────

/**
 * detailed_violations → Map<"nurseId-dayIndex", CellViolationInfo>
 *
 * 모든 매핑 로직(isError, messages, groups)을 한 곳에서 pre-compute.
 * UI 컴포넌트에서는 map.get(key)로 O(1) lookup만 수행하면 됨.
 *
 * @param detailedViolations - useRosterValidation에서 생성된 검증 결과
 * @param copyNurses - 전체 간호사 목록 (스케줄 포함)
 * @param options - 시프트 코드 목록, 프리셉티 정책 등
 * @returns Map<"nurseId-dayIndex", CellViolationInfo>
 */
export function buildCellViolationMap(
  detailedViolations: DetailedViolation[],
  copyNurses: NurseForCellMap[],
  options: BuildCellMapOptions
): Map<string, CellViolationInfo> {
  const map = new Map<string, CellViolationInfo>();

  if (
    !detailedViolations ||
    detailedViolations.length === 0 ||
    !copyNurses ||
    copyNurses.length === 0
  ) {
    return map;
  }

  const {
    availableShifts,
    disableManpowerValidation,
    precepteePolicy,
    tailDaysCount = 0,
    manpowerValidateShiftKeys,
    includeMidInManpowerGroupMap,
    nurseLockedDaysMap,
  } = options;

  /** 1-based day → 0-based dayIndex 변환 후 잠금 여부 반환 */
  const isDayLockedForNurse = (nurseId: string, dayIndex: number): boolean => {
    if (!nurseLockedDaysMap) return false;
    const locked = nurseLockedDaysMap.get(nurseId);
    if (!locked) return false;
    return locked.has(dayIndex + 1); // lockedDays는 1-based
  };
  const tailStart = tailDaysCount; // 음수 인덱스 허용 범위: -tailStart ~ daysInMonth-1

  // 1. id 기반 조회 맵 (validateManpower와 동일 해석)
  const lookupMaps = buildShiftLookupMaps(availableShifts);

  // 2. daysInMonth 계산
  const daysInMonth = copyNurses[0]?.schedule?.length ?? 0;
  if (daysInMonth === 0) return map;

  const manpowerAgg =
    manpowerValidateShiftKeys != null &&
    manpowerValidateShiftKeys.length > 0
      ? {
          validateKeys: new Set(manpowerValidateShiftKeys),
          groupLetterMap: buildShiftIdToGroupMainLetterMap(availableShifts, {
            includeMid: includeMidInManpowerGroupMap !== false,
          }),
        }
      : undefined;

  // 3. groupTotalsByDay (manpower_shortage) — 서브 shift_id도 그룹 대표 문자로 합산
  const groupTotalsByDay = buildGroupTotalsByDay(
    copyNurses,
    lookupMaps,
    daysInMonth,
    precepteePolicy,
    manpowerAgg,
    nurseLockedDaysMap
  );

  // 4. nurse_id 인덱스 구축 (빠른 조회용)
  const nurseMap = new Map<string, NurseForCellMap>();
  copyNurses.forEach((n) => {
    const id = n.nurse_id || n.id;
    if (id) nurseMap.set(id, n);
  });

  // 5. 위반별로 영향받는 셀들에 정보 추가
  detailedViolations.forEach((v) => {
    // ── manpower_shortage/manpower_excess: 모든 간호사 × 해당 일 ──
    if (v.type === "manpower_shortage" || v.type === "manpower_excess") {
      if (disableManpowerValidation) return;
      if (v.day === undefined || v.shift === undefined) return;

      const dayIndex = v.day;

      copyNurses.forEach((nurse) => {
        const nurseId = nurse.nurse_id || nurse.id;
        if (!nurseId || !nurse.schedule) return;

        // 프리셉티 UI 제외
        if (
          precepteePolicy.excludePrecepteeFromUi &&
          nurse.preceptor?.role === "preceptee"
        ) {
          return;
        }

        // 잠금 day는 위반 표시 제외
        if (isDayLockedForNurse(nurseId, dayIndex)) return;

        const code = nurse.schedule![dayIndex];
        if (!code) return;

        // id 기반 해석: validateManpower·RosterMadeTable 통계와 동일
        const scheduleId = nurse.schedule_ids?.[dayIndex] ?? null;
        const cellResolved = resolveShiftFromScheduleCellFromMaps(
          code,
          scheduleId,
          lookupMaps
        );
        const cellBucket = manpowerAgg
          ? getManpowerBucketForResolvedShift(
              cellResolved,
              manpowerAgg.groupLetterMap,
              manpowerAgg.validateKeys
            )
          : cellResolved?.default_shift ?? null;

        const isMatch = v.shift != null && v.shift === cellBucket;

        if (!isMatch) return;

        // groupTotal 체크 — validateManpower와 동일하게 집계된 값 사용
        const groupTotal =
          groupTotalsByDay.get(`${dayIndex}-${v.shift}`) || 0;
        if (v.type === "manpower_shortage" && groupTotal >= (v.required ?? 0)) {
          return;
        }
        if (
          v.type === "manpower_excess" &&
          groupTotal <= (v.required_max ?? Number.POSITIVE_INFINITY)
        ) {
          return;
        }

        const key = `${nurseId}-${dayIndex}`;
        const cell = getOrCreateCell(map, key);
        cell.isError = true;

        const msg = buildViolationMessage(
          v,
          nurse.name,
          dayIndex,
          groupTotal,
          options.formatDayLabel
        );
        if (msg) cell.messages.push(msg);

        const group = buildViolationGroup(
          v,
          dayIndex,
          groupTotal,
          v.type === "manpower_shortage" ? v.required : v.required_max,
          tailStart,
          daysInMonth
        );
        if (group && !hasGroupDuplicate(cell.groups, group)) {
          cell.groups.push(group);
        }
      });
      return;
    }

    // ── max_nig_per_month: 해당 간호사의 모든 셀 ──
    if (v.type === "max_nig_per_month") {
      if (!v.nurse_id) return;
      const nurse = nurseMap.get(v.nurse_id);
      if (!nurse) return;
      if (
        precepteePolicy.excludePrecepteeFromUi &&
        nurse.preceptor?.role === "preceptee"
      ) {
        return;
      }

      const nurseName = nurse.name || v.nurse_id;
      for (let dayIndex = 0; dayIndex < daysInMonth; dayIndex++) {
        const key = `${v.nurse_id}-${dayIndex}`;
        const cell = getOrCreateCell(map, key);
        cell.isError = true;

        const msg = buildViolationMessage(v, nurseName, dayIndex, undefined, options.formatDayLabel);
        if (msg) cell.messages.push(msg);
        // max_nig_per_month는 시각적 그룹 바 없음
      }
      return;
    }

    // ── range-based 위반: 해당 간호사 × 날짜 범위 ──
    if (!v.nurse_id) return;
    const nurse = nurseMap.get(v.nurse_id);
    if (!nurse) return;
    if (
      precepteePolicy.excludePrecepteeFromUi &&
      nurse.preceptor?.role === "preceptee"
    ) {
      return;
    }

    const nurseName = nurse.name || v.nurse_id;
    const range = getViolationRange(v);

    // banned_day_after_eve는 day/next_day 구조
    if (v.type === "banned_day_after_eve") {
      if (v.day === undefined || v.next_day === undefined) return;
      const days = [v.day, v.next_day];
      days.forEach((dayIndex) => {
        if (dayIndex < -tailStart || dayIndex >= daysInMonth) return;
        if (v.nurse_id && isDayLockedForNurse(v.nurse_id, dayIndex)) return;
        const key = `${v.nurse_id}-${dayIndex}`;
        const cell = getOrCreateCell(map, key);

        const msg = buildViolationMessage(v, nurseName, dayIndex, undefined, options.formatDayLabel);
        if (msg) cell.messages.push(msg);

        const group = buildViolationGroup(
          v,
          dayIndex,
          undefined,
          undefined,
          tailStart,
          daysInMonth
        );
        if (group && !hasGroupDuplicate(cell.groups, group)) {
          cell.groups.push(group);
        }

        // 연속 위반은 그룹 바(violation-group-bar)로만 표시하고,
        // 개별 셀 ring(border)은 제거합니다.
        cell.isError = false;
      });
      return;
    }

    // range-based: max_conseq_work, three_seq_nig, two_offs_after_*
    if (!range) return;

    for (
      let dayIndex = range.startDay;
      dayIndex <= range.endDay;
      dayIndex++
    ) {
      if (dayIndex < -tailStart || dayIndex >= daysInMonth) continue;
      if (v.nurse_id && isDayLockedForNurse(v.nurse_id, dayIndex)) continue;

      const key = `${v.nurse_id}-${dayIndex}`;
      const cell = getOrCreateCell(map, key);

      const msg = buildViolationMessage(v, nurseName, dayIndex, undefined, options.formatDayLabel);
      if (msg) cell.messages.push(msg);

      const group = buildViolationGroup(
        v,
        dayIndex,
        undefined,
        undefined,
        tailStart,
        daysInMonth
      );
      if (group && !hasGroupDuplicate(cell.groups, group)) {
        cell.groups.push(group);
      }

      // 연속 위반은 그룹 바(violation-group-bar)로만 표시하고,
      // 개별 셀 ring(border)은 제거합니다.
      cell.isError = false;
    }
  });

  return map;
}
